import { CHUNK_SIZE } from '../../shared/shared'
import { getChunkTileIndexAtWorld, getChunkTileWorld } from '../../shared/Chunks'
import { tilesetCellKey, tilesetKey } from '../../shared/Tilesets'
import { tilesetAutotileKey } from '../../shared/TilesetAutotiles'

export class ClientChunks {
  constructor(world) {
    this.world = world
    this.byId = new Map()
  }

  get(id) {
    return this.byId.get(id) || null
  }

  add(data) {
    this.remove(data.id)
    this.byId.set(data.id, new Chunk(this.world, data))
  }

  remove(chunkId) {
    const chunk = this.byId.get(chunkId)
    if (!chunk) return
    chunk.destroy()
    this.byId.delete(chunkId)
  }

  getAtWorld(x, y) {
    const chunkX = Math.floor(x / CHUNK_SIZE)
    const chunkY = Math.floor(y / CHUNK_SIZE)
    return this.byId.get(`${chunkX}_${chunkY}`) || null
  }

  applyTileChanges(changes) {
    for (const change of changes) {
      const chunk = this.getAtWorld(change.x, change.y)
      if (!chunk) continue
      if (change.tile != null) {
        chunk.setTileDescAtWorld(change.x, change.y, change.tile)
      } else {
        chunk.clearTileAtWorld(change.x, change.y, change.layer)
      }
    }
  }

  applyFlagChanges(changes) {
    for (const change of changes) {
      const chunk = this.getAtWorld(change.x, change.y)
      if (!chunk) continue
      chunk.setFlagsAtWorld(change.x, change.y, change.flags)
      this.world.flagOverlay?.update(chunk)
    }
  }

  getFlagsAtWorld(x, y) {
    const fx = Math.floor(x)
    const fy = Math.floor(y)
    return this.getAtWorld(fx, fy)?.getFlagsAtWorld(fx, fy) ?? 0
  }
}

class Chunk {
  constructor(world, data) {
    this.world = world
    this.id = data.id
    this.x = parseInt(data.id.split('_')[0])
    this.y = parseInt(data.id.split('_')[1])
    this.palette = data.palette
    this.tiles = data.tiles
    this.flags = data.flags || new Array(CHUNK_SIZE * CHUNK_SIZE).fill(0)
    this.colliders = new Map()
    this._paletteMap = new Map()
    this._rebuildPaletteMap()
    this.build()
  }

  _rebuildPaletteMap() {
    this._paletteMap.clear()
    for (let i = 1; i < this.palette.length; i++) {
      const entry = this.palette[i]
      if (typeof entry === 'object' && entry !== null) {
        this._paletteMap.set(JSON.stringify(entry), i)
      }
    }
  }

  _paletteIndexForDesc(desc) {
    const key = JSON.stringify(desc)
    let idx = this._paletteMap.get(key)
    if (idx == null) {
      idx = this.palette.length
      this.palette.push(desc)
      this._paletteMap.set(key, idx)
    }
    return idx
  }

  build() {
    for (let idx = 0; idx < this.tiles.length; idx++) {
      this.buildTile(idx)
    }
  }

  destroy() {
    for (let idx = 0; idx < this.tiles.length; idx++) {
      if (!this.tiles[idx]) continue
      this.clearTileVisual(idx)
    }
    for (const collider of this.colliders.values()) {
      collider.destroy()
    }
    this.colliders.clear()
  }

  getTileStateAtWorld(x, y, layer) {
    const idx = getChunkTileIndexAtWorld(x, y, layer)
    const paletteIdx = this.tiles[idx]
    return paletteIdx ? this.palette[paletteIdx] : null
  }

  setTileDescAtWorld(wx, wy, desc) {
    const idx = getChunkTileIndexAtWorld(wx, wy, desc.layer)
    const prevIdx = this.tiles[idx]
    const paletteIdx = this._paletteIndexForDesc(desc)
    if (prevIdx === paletteIdx) return false
    this.tiles[idx] = paletteIdx
    this.buildTile(idx)
    return true
  }

  getFlagsAtWorld(x, y) {
    const lx = ((x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE
    const ly = ((y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE
    return this.flags[ly * CHUNK_SIZE + lx]
  }

  setFlagsAtWorld(x, y, value) {
    const lx = ((x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE
    const ly = ((y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE
    this.flags[ly * CHUNK_SIZE + lx] = value
  }

  clearTileAtWorld(x, y, layer) {
    const idx = getChunkTileIndexAtWorld(x, y, layer)
    if (!this.tiles[idx]) return false
    this.clearTileVisual(idx)
    this.tiles[idx] = 0
    return true
  }

  buildTile(idx) {
    this.clearCollider(idx)

    const paletteIdx = this.tiles[idx]
    if (!paletteIdx) return

    const entry = this.palette[paletteIdx]
    if (!entry) return

    const { x, y, layer } = getChunkTileWorld(this.x, this.y, idx)

    let key
    if ('bits' in entry) {
      key = tilesetAutotileKey(entry.ts, entry.x, entry.y, entry.bits, entry)
    } else {
      const allFrames = [[entry.x, entry.y], ...(entry.frames || [])]
      key = allFrames.length > 1
        ? tilesetKey(entry.ts, allFrames)
        : tilesetCellKey(entry.ts, entry.x, entry.y)
    }
    this.world.tiles.setVisual(layer, x, y, key, entry.fps || 0, entry.yBase || 0)
    this.setCollider(idx, x, y, entry.collider)
  }

  clearTileVisual(idx) {
    const { x, y, layer } = getChunkTileWorld(this.x, this.y, idx)
    this.world.tiles.clearVisual(layer, x, y)
    this.clearCollider(idx)
  }

  setCollider(idx, x, y, collider) {
    if (!collider) return
    this.colliders.set(
      idx,
      this.world.physics.add(
        x,
        y,
        { type: 'solid', ox: collider.ox, oy: collider.oy, w: collider.w, h: collider.h },
        { kind: 'tile', chunkId: this.id }
      )
    )
  }

  clearCollider(idx) {
    const collider = this.colliders.get(idx)
    if (!collider) return
    collider.destroy()
    this.colliders.delete(idx)
  }
}
